import { useCallback, useMemo, useState, useEffect } from 'react'
import UAccordionHeader from './UAccordionHeader'
import RegionDropDown from '../common/CommonFieldWidgets/RegionDropDown'
import {
  APP_ID_BACKBONE,
  APP_ID_LOGGING_SERVICE,
  APP_ID_PANORAMA,
  APP_ID_SUPPORT,
  REGION_FIELD_NAME,
} from '../common/GlobalConstants'
import { CONTEXT_TO_APP_ID } from '../../constants'
import { updateStateProps } from '../../actions'
import { useFormContext } from 'react-hook-form'

import type { VFC } from 'react'
import type { IDropDownData } from '../common/DropDown/DropDown'
import type { CommonStoreState, ContextApp } from '../../store'
import type { Instance } from '@sparky/framework'
import type { Actions } from '../../actions'
import type { BannerProps } from './UAccordion'

const EXCLUDE_REGION_APPS = new Set([APP_ID_SUPPORT, APP_ID_PANORAMA, APP_ID_BACKBONE])

export type URegionComponentProp = {
  title: string
  subtitle: string
  state: CommonStoreState
  actions: Actions
  updateTitle: (title: string) => void
  setAccordionOpen: (isOpen: boolean) => void
  setDisable: (disable: boolean) => void
  setEditDisable: (disable: boolean) => void
  setBanner: (banner: BannerProps) => void
}

const defaultProps = {
  title: 'Select Region',
  subtitle: 'Select the region nearest to the storage location of the data logs.',
}

const URegionComponent: VFC<URegionComponentProp> = (props) => {
  const { title, subtitle, state, updateTitle, setAccordionOpen, setDisable, setEditDisable, setBanner } = props
  const { loadingTsg, licenses, context, instances, lockedInfo } = state
  const ContextToAppId: ContextApp = CONTEXT_TO_APP_ID

  const [instance, setInstance] = useState<Instance | null>(null)
  const { watch } = useFormContext()
  const currentRegion = watch(REGION_FIELD_NAME)

  const onRegionChange = useCallback((value: IDropDownData) => {
    updateTitle(`Region: ${value.value}`)
    updateStateProps({ selectedPlatformRegion: value.id })
    setAccordionOpen(false)
  }, [setAccordionOpen, updateTitle])

  const regionDisabled = useMemo(() => {
    // disable only if set by lockedInfo, if only 1 region available, don't disable and let user choose
    return Boolean(lockedInfo?.regions?.length === 1)
  }, [lockedInfo?.regions?.length])

  const errorCallback = useCallback((message: string) => {
    setBanner({
      display: true,
      message,
      appearance: 'error',
    })
  }, [setBanner])

  useEffect(() => {
    if (currentRegion) {
      updateStateProps({ selectedPlatformRegion: currentRegion.id })
      updateTitle(`Region: ${currentRegion.value}`)
    }
  }, [currentRegion, updateTitle])

  useEffect(() => {
    if (regionDisabled) {
      setEditDisable(true)
      setAccordionOpen(false)
    }
    else {
      setEditDisable(false)
    }
  }, [regionDisabled, setAccordionOpen, setEditDisable])

  useEffect(() => {
    if (!instances.length) {
      setInstance(null)
      return
    }
    setInstance(instances.find(each => (each.platform_region && each.platform_region !== 'global') || each.app_id === APP_ID_LOGGING_SERVICE) || null)
  }, [instances])

  useEffect(() => {
    if (state.supportAccountId && (state.tsg || state.tsgs[0].children?.length === 0)) {
      setDisable(false)
    }
    else {
      setDisable(true)
    }
  }, [setDisable, state.supportAccountId, state.tsg, state.tsgs])

  const regionProps = useMemo(() => {
    const callbacks = {
      changeHandlerCallback: onRegionChange
    }
    const _appIdList: string[] = licenses?.map((each: any) => each.app_id).filter(each => !EXCLUDE_REGION_APPS.has(each)) ||
      (context && [ContextToAppId[context]]) ||
      []

    return {
      callbacks,
      apps: state.apps,
      appIdList: _appIdList,
      isLoading: loadingTsg,
      disable: false,
      instance,
      state,
      support_account_id: state.supportAccountId,
    }
  }, [onRegionChange, licenses, context, ContextToAppId, state, loadingTsg, instance])
  return (
    <div>
      <UAccordionHeader title={title || defaultProps.title} subtitle={subtitle || defaultProps.title} />
      <RegionDropDown {...regionProps} errorCallback={errorCallback} />
    </div>
  )

}

export default URegionComponent
